import React, { useState, useEffect } from "react";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useDropzone } from "react-dropzone";
import { Trash2 } from "lucide-react";
import { getBackendUrl } from "../../config";

import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === 'dark';
  return {
    root: {
      "& .MuiBackdrop-root": {
        backgroundColor: dark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.3)',
        backdropFilter: 'blur(4px)',
      },
      "& .MuiDialog-paper": {
        borderRadius: 12,
        overflow: "hidden",
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: dark
          ? '0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
          : '0 10px 24px rgba(16, 24, 40, 0.10), 0 4px 8px rgba(16, 24, 40, 0.06)',
        backgroundColor: theme.palette.background.paper,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      },
    },
    alignTop: {
      "& .MuiDialog-container": {
        alignItems: "flex-start",
      },
      "& .MuiDialog-paper": {
        marginTop: theme.spacing(4),
      },
    },
    dialogTitle: {
      position: "relative",
      background: dark ? theme.palette.background.contrast : theme.palette.background.default,
      borderBottom: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(1.5, 2),
      "& h2": {
        display: "flex",
        alignItems: "center",
        fontSize: "1rem",
      },
    },
    titleRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(1),
    },
    titleActions: {
      display: "flex",
      alignItems: "center",
    },
    dialogContent: {
      padding: theme.spacing(2),
      minHeight: 90,
      backgroundColor: 'transparent',
      overflowY: "visible",
      overflowX: "hidden",
      // 📱 Responsivo: em mobile/tablet o conteúdo passa a rolar (era overflowY:visible
      // = sem scroll, cortava forms longos); no desktop preserva o comportamento atual.
      [theme.breakpoints.down("sm")]: {
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
      },
    },
    dialogActions: {
      padding: theme.spacing(1.5, 2),
      background: dark ? theme.palette.background.contrast : theme.palette.background.default,
      borderTop: `1px solid ${theme.palette.divider}`,
      justifyContent: "flex-end",
    },
    actionButton: {
      borderRadius: 8,
      padding: theme.spacing(0.75, 2),
      boxShadow: "none",
      textTransform: "none",
      fontWeight: 600,
      fontSize: "0.85rem",
      minWidth: 100,
    },
    equalButton: {
      flex: 1,
    },
    cancelButton: {
      marginRight: theme.spacing(1),
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
    },
    secondaryButton: {
      marginRight: theme.spacing(1),
    },
    icon: {
      marginRight: theme.spacing(1),
      fontSize: "1.2rem",
      color: theme.palette.primary.main,
    },
    title: {
      fontSize: "1rem",
      display: "flex",
      alignItems: "center",
    },
    dropzone: {
      border: `1px dashed ${theme.palette.divider}`,
      padding: theme.spacing(1),
      textAlign: "center",
      cursor: "pointer",
      borderRadius: theme.shape.borderRadius,
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      backgroundColor: theme.palette.background.default,
      "&:hover": {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.action.hover,
      },
    },
    imageContainer: {
      position: "relative",
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    previewImage: {
      width: "100%",
      maxHeight: 120,
      objectFit: "contain",
      borderRadius: theme.shape.borderRadius,
      border: `1px solid ${theme.palette.divider}`,
    },
    deleteButton: {
      position: "absolute",
      top: 4,
      right: 4,
      backgroundColor: dark ? "rgba(0, 0, 0, 0.6)" : "rgba(255, 255, 255, 0.8)",
      color: dark ? theme.palette.text.primary : theme.palette.text.secondary,
      borderRadius: "50%",
      padding: 4,
      minWidth: "unset",
      width: 30,
      height: 30,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "&:hover": {
        backgroundColor: dark ? "rgba(0, 0, 0, 0.8)" : "rgba(255, 255, 255, 0.95)",
      },
    },
    dropzoneText: {
      fontSize: "0.85rem",
      color: theme.palette.text.secondary,
    },
    backdrop: {
      backdropFilter: dark ? "blur(10px) saturate(160%)" : "blur(8px)",
      WebkitBackdropFilter: dark ? "blur(10px) saturate(160%)" : "blur(8px)",
      backgroundColor: dark ? "rgba(0, 0, 0, 0.5)" : "rgba(0, 0, 0, 0.35)",
    },
  };
});

const StandardModal = ({
  title,
  children,
  open,
  onClose,
  onConfirm,
  maxWidth = "xs",
  titleIcon,
  titleActions = null,
  alignTop = false,

  primaryButton = {
    text: i18n.t("confirmationModal.buttons.confirm"),
    color: "primary",
    onClick: null,
    visible: true,
    disabled: false,
    loading: false,
    startIcon: null,
  },
  secondaryButton = {
    text: i18n.t("confirmationModal.buttons.cancel"),
    color: "secondary",
    onClick: null,
    visible: true,
    disabled: false,
    startIcon: null,
  },
  tertiaryButton = {
    text: "",
    color: "",
    onClick: null,
    visible: false,
    disabled: false,
    startIcon: null,
    align: "right",
    className: "",
  },

  confirmationText,
  cancelText,
  confirmButtonColor = "primary",
  showFooter = true,
  loading = false,
  disableConfirmButton = false,
  disableCancelButton = false,
  equalButtons = false,
  useButtonSpinner = false, // eslint-disable-line no-unused-vars
  customOnClose,
  closeOnConfirm = true,
  disableBackdropClick = false,
  disableEscapeKeyDown = false,

  enableDropzone = false,
  acceptedFiles = { "image/*": [] },
  maxFiles = 1,
  dropzoneText = "Arraste e solte um arquivo aqui, ou clique para selecionar",
  initialAttachment = null,
  onDropAccepted = null,
  onDeleteAttachment = null,
  zIndex,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));
  const [attachment, setAttachment] = useState(initialAttachment);

  useEffect(() => {
    setAttachment(initialAttachment);
  }, [initialAttachment]);

  const { getRootProps, getInputProps } = useDropzone({
    accept: acceptedFiles,
    multiple: maxFiles > 1,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        setAttachment(maxFiles === 1 ? acceptedFiles[0] : acceptedFiles);
        if (onDropAccepted) {
          onDropAccepted(maxFiles === 1 ? acceptedFiles[0] : acceptedFiles);
        }
      }
    },
  });

  if (confirmationText) primaryButton.text = confirmationText;
  if (cancelText) secondaryButton.text = cancelText;
  if (confirmButtonColor) primaryButton.color = confirmButtonColor;
  // Spinner visível permanece removido (decisão de produto). Mas, para evitar
  // duplo-submit, loading=true DESABILITA o botão de confirmar enquanto a ação roda.
  // Respeita também os disabled passados pelos callers e os overrides booleanos.
  primaryButton.disabled = Boolean(primaryButton.disabled || disableConfirmButton || loading);
  secondaryButton.disabled = Boolean(secondaryButton.disabled || disableCancelButton);

  const handleClose = () => {
    if (customOnClose) {
      customOnClose();
    } else {
      onClose(false);
    }
  };

  const handleConfirm = () => {
    if (primaryButton.onClick) {
      primaryButton.onClick(attachment);
    } else if (onConfirm) {
      onConfirm(attachment);
      if (closeOnConfirm) {
        onClose(false);
      }
    } else if (closeOnConfirm) {
      onClose(false);
    }
  };

  const handleSecondaryClick = () => {
    if (secondaryButton.onClick) {
      secondaryButton.onClick();
    } else {
      handleClose();
    }
  };

  const handleDeleteAttachment = () => {
    setAttachment(null);
    if (onDeleteAttachment) {
      onDeleteAttachment();
    }
  };

  // Loading spinner removido por solicitação
  const ConfirmButton = Button;

  const getImageUrl = (file) => {
    if (!file) return null;
    if (typeof file === "string") {
      if (file.startsWith("http://") || file.startsWith("https://")) {
        return file;
      }
      return `${getBackendUrl()}/public/${file}`;
    }
    return URL.createObjectURL(file);
  };

  return (
    <Dialog
      open={open}
      fullScreen={fullScreen}
      onClose={(event, reason) => {
        if (disableBackdropClick && reason === "backdropClick") return;
        if (disableEscapeKeyDown && reason === "escapeKeyDown") return;
        handleClose();
      }}
      className={`${classes.root} ${alignTop ? classes.alignTop : ""}`}
      BackdropProps={{
        className: classes.backdrop,
      }}
      fullWidth
      maxWidth={maxWidth}
      style={zIndex ? { zIndex } : undefined}
      PaperProps={{
        elevation: 0, // Remove a sombra dura padrão do Material-UI
        style: { borderRadius: 12 },
      }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <div className={classes.titleRow}>
          <Typography
            className={classes.title}
            variant="subtitle1"
            component="span"
          >
            {titleIcon &&
              React.cloneElement(titleIcon, { className: classes.icon })}
            {title}
          </Typography>
          {titleActions && (
            <div className={classes.titleActions}>{titleActions}</div>
          )}
        </div>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {children}

        {enableDropzone && (
          <div>
            {!attachment ? (
              <div {...getRootProps()} className={classes.dropzone}>
                <input {...getInputProps()} />
                <p className={classes.dropzoneText}>{dropzoneText}</p>
              </div>
            ) : (
              <div className={classes.imageContainer}>
                {(typeof attachment === "object" &&
                  attachment.type?.startsWith("image/")) ||
                typeof attachment === "string" ? (
                  <img
                    src={getImageUrl(attachment)}
                    alt="Preview"
                    className={classes.previewImage}
                    onError={(e) => {
                      e.target.onerror = null;
                    }}
                  />
                ) : (
                  <div className={classes.fileInfo}>
                    {typeof attachment === "object"
                      ? attachment.name
                      : "Arquivo anexado"}
                  </div>
                )}
                <Button
                  size="small"
                  className={classes.deleteButton}
                  onClick={handleDeleteAttachment}
                >
                  <Trash2 size={18} />
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
      {showFooter && (
        <DialogActions className={classes.dialogActions}>
          {tertiaryButton.visible && tertiaryButton.align === "left" && (
            <div style={{ marginRight: "auto" }}>
              <Button
                variant="outlined"
                onClick={tertiaryButton.onClick}
                color={tertiaryButton.color}
                className={`${classes.actionButton} ${
                  classes.secondaryButton
                } ${tertiaryButton.className || ""}`}
                disabled={tertiaryButton.disabled}
                startIcon={tertiaryButton.startIcon}
              >
                {tertiaryButton.text}
              </Button>
            </div>
          )}

          {secondaryButton.visible !== false && (
            <Button
              variant="outlined"
              onClick={handleSecondaryClick}
              color={secondaryButton.color || "default"}
              className={`${classes.actionButton} ${classes.cancelButton} ${equalButtons ? classes.equalButton : ""}`}
              disabled={secondaryButton.disabled}
              startIcon={secondaryButton.startIcon}
            >
              {secondaryButton.text}
            </Button>
          )}

          {tertiaryButton.visible && tertiaryButton.align !== "left" && (
            <Button
              variant="outlined"
              onClick={tertiaryButton.onClick}
              color={tertiaryButton.color}
              className={`${classes.actionButton} ${classes.secondaryButton} ${
                tertiaryButton.className || ""
              } ${equalButtons ? classes.equalButton : ""}`}
              disabled={tertiaryButton.disabled}
              startIcon={tertiaryButton.startIcon}
            >
              {tertiaryButton.text}
            </Button>
          )}

          {primaryButton.visible !== false && (
            <ConfirmButton
              variant="contained"
              onClick={handleConfirm}
              color={primaryButton.color || "primary"}
              className={`${classes.actionButton} ${equalButtons ? classes.equalButton : ""}`}
              disabled={primaryButton.disabled}
              startIcon={primaryButton.startIcon}
            >
              {primaryButton.text}
            </ConfirmButton>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default StandardModal;
